/**
 * Route guard (CLAUDE.md 11.7).
 *
 * Purely a navigation convenience. It decides what to *render*, never what is
 * permitted -- every endpoint authorises independently, so a user who edited their
 * way past this gains nothing but a page full of 404s.
 *
 * Waits for the session probe rather than redirecting immediately: bouncing a
 * signed-in user to /login for a moment on every reload is worse than a brief spinner.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '@/auth/AuthProvider'
import { FullPageSpinner } from '@/components/layout/FullPageSpinner'
import { NotFoundPage } from '@/pages/NotFoundPage'

export function ProtectedRoute() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageSpinner />

  if (!user) {
    // `from` lets the login page send them back where they were headed.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  // The first-run administrator's password came from a file on disk. Until it is
  // replaced, that file is the credential, so nothing else is reachable (section 19).
  if (user.password_change_required && location.pathname !== '/settings/password') {
    return <Navigate to="/settings/password" replace />
  }

  return <Outlet />
}

/**
 * Server administration (12.2).
 *
 * Sends a non-sysadmin to the not-found page rather than to a "forbidden" screen: the
 * existence of an administration section is not something an ordinary user needs
 * confirmed, and it matches what the API does for a resource you may not see (15.5).
 *
 * As above, this is navigation only. Every `/admin` endpoint depends on
 * `require_sysadmin` server-side, so editing the URL buys nothing.
 */
export function SysadminRoute() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageSpinner />
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }
  // Rendered in place rather than redirected to, so the URL the user typed stays in
  // the bar and nothing new appears in their history.
  if (user.system_role !== 'sysadmin') return <NotFoundPage />

  return <Outlet />
}

/** The inverse: keeps a signed-in user off the login and registration pages. */
export function PublicOnlyRoute() {
  const { user, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  if (user) return <Navigate to="/" replace />
  return <Outlet />
}
